import { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import PageMeta from "@/components/PageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, AlertTriangle, CalendarClock, CheckCircle2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { PHASES, DELAYS, FAQS } from "@/components/tools/homeBuyingTimelineData";
import RelatedTools from "@/components/tools/RelatedTools";
import { PAGE_URL } from "@/data/homeBuyingTimelineSchema";


const LAST_REVIEWED = "August 2026";

const CONTENTS = [
  ...PHASES.map((p) => ({ id: p.id, label: `${p.name} — ${p.duration}` })),
  { id: "delays", label: "Common delays and what to do" },
  { id: "closing-checklist", label: "Closing week checklist" },
  { id: "faq", label: "Common questions" },
];

const CLOSING_CHECKLIST = [
  {
    title: "Homeowners insurance",
    body: "Bind a policy effective on the closing date and send the declaration page to your lender. No policy, no funding.",
  },
  {
    title: "Wire instructions",
    body: "Call the title company at a number you looked up yourself to verify wiring details. Never trust instructions that arrive by email, even from a familiar address.",
  },
  {
    title: "Utilities",
    body: "Schedule electric, gas, water, trash, and internet to transfer on the closing date. Some providers need several business days of notice.",
  },
  {
    title: "Address change",
    body: "File with USPS, then update your employer, bank, insurers, subscriptions, voter registration, and driver's license.",
  },
  {
    title: "Final cash to close",
    body: "Confirm the exact figure on the Closing Disclosure, which you receive at least three business days before signing. Wire or bring certified funds per your closer's instructions.",
  },
  {
    title: "Photo ID",
    body: "A current government-issued photo ID for every person signing. Notaries will not proceed without it.",
  },
  {
    title: "At the closing table",
    body: "You sign the note, the mortgage or deed of trust, and the settlement statement. Funds are wired, the deed is recorded, and keys transfer — same day in many states, next business day in others.",
  },
];

const HomeBuyingTimeline = () => {
  const [openFaq, setOpenFaq] = useState<string>("");
  const [openChecklists, setOpenChecklists] = useState<string[]>([]);

  useEffect(() => {
    trackEvent("timeline_guide_view", { entry_section: "page_load" });
  }, []);

  const handleFaqChange = (value: string) => {
    setOpenFaq(value);
    if (value) trackEvent("faq_open", { entry_section: "faq", option_type: value });
  };

  const handleChecklistChange = (values: string[]) => {
    const added = values.find((v) => !openChecklists.includes(v));
    setOpenChecklists(values);
    if (added) {
      trackEvent("checklist_expand", {
        phase_name: added,
        section_name: added,
        checklist_type: "phase",
      });
    }
  };

  const handlePhaseClick = (phase: string) =>
    trackEvent("timeline_phase_click", { phase_name: phase, section_name: "on_this_page" });

  const handleLeadStart = (section: string) =>
    trackEvent("lead_form_start", { section_name: section, phase_name: section });



  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageMeta
        title="Home Buying Timeline: Every Step From Start to Closing Day"
        description="A realistic list of every step from pre-approval to closing day — 20 chronological milestones, how long each phase actually takes, the delays that push closings back, and a closing week checklist."
        path="/tools/home-buying-timeline"
      />
      <Header />

      <main className="flex-1">
        <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardContent className="p-5 sm:p-6">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-primary">Short answer</p>
              <p className="text-base leading-relaxed text-foreground sm:text-lg">
                Start to closing day usually runs <strong>three to six months</strong>: about 2 to 4 weeks of
                pre-work, 1 to 4 months of house hunting, 1 to 3 days from offer to signed contract, and{" "}
                <strong>30 to 45 days under contract</strong> once you are in escrow. The house hunt is what varies
                most; the under-contract phase is the part that runs on hard deadlines.
              </p>
            </CardContent>
          </Card>

          <h1 className="mb-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Home Buying Timeline: Every Step From Pre-Approval to Closing Day
          </h1>
          <p className="mb-3 text-base text-muted-foreground sm:text-lg">
            Twenty chronological steps across five phases, with realistic durations for each one, the hurdles that
            quietly add weeks, and a closing week checklist so nothing lands on you the day before signing.
          </p>
          <p className="mb-6 text-xs text-muted-foreground">
            Last reviewed: {LAST_REVIEWED}. Timelines, fees, contract deadlines, and closing practices vary by
            lender, market, and state. Use these ranges as a planning baseline, not a promise.
          </p>

          <Card className="mb-8 border-primary/30 bg-primary/5">
            <CardContent className="p-5 sm:p-6">
              <div className="mb-3 flex items-center gap-2">
                <CalendarClock className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                <p className="font-bold text-foreground">The whole timeline at a glance</p>
              </div>
              <ul className="grid gap-2 sm:grid-cols-2">
                {PHASES.map((p) => (
                  <li key={p.id} className="rounded-lg border border-border bg-card px-3 py-2">
                    <p className="text-sm font-semibold text-foreground">{p.name}</p>
                    <p className="text-sm text-primary">{p.duration}</p>
                  </li>
                ))}
                <li className="rounded-lg border border-primary/40 bg-primary/10 px-3 py-2">
                  <p className="text-sm font-semibold text-foreground">Total, start to keys</p>
                  <p className="text-sm text-primary">3 to 6 months</p>
                </li>
              </ul>
            </CardContent>
          </Card>

          <nav
            aria-label="On this page"
            className="sticky top-16 z-20 mb-10 rounded-xl border border-border bg-card/95 p-5 shadow-sm backdrop-blur print:hidden"
          >
            <p className="mb-3 text-sm font-bold text-foreground">Jump to a phase</p>
            <ul className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {CONTENTS.map((c) => (
                <li key={c.id}>
                  <a
                    href={`#${c.id}`}
                    className="text-primary hover:underline"
                    onClick={() => handlePhaseClick(c.id)}
                  >
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {PHASES.map((phase) => (
            <section key={phase.id} id={phase.id} className="mb-12 scroll-mt-24">
              <div className="mb-3 flex flex-wrap items-baseline gap-3">
                <h2 className="text-2xl font-bold text-foreground">{phase.name}</h2>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                  {phase.duration}
                </span>
              </div>
              <p className="mb-5 text-sm leading-relaxed text-muted-foreground">{phase.summary}</p>

              <ol className="mb-5 space-y-4 border-l-2 border-border pl-5">
                {phase.steps.map((s) => (
                  <li key={s.n} className="relative">
                    <span
                      className="absolute -left-[1.7rem] flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground"
                      aria-hidden
                    >
                      {s.n}
                    </span>
                    <p className="font-semibold text-foreground">{s.title}</p>
                    <p className="text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                  </li>
                ))}
              </ol>

              <Accordion
                type="multiple"
                value={openChecklists}
                onValueChange={handleChecklistChange}
                className="rounded-xl border border-border bg-card px-4 [&_[role=region][data-state=closed]]:hidden"
              >
                <AccordionItem value={phase.id} className="border-none">
                  <AccordionTrigger className="text-left text-sm font-semibold">
                    {phase.name} checklist ({phase.checklist.length} items)
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 pb-2">
                      {phase.checklist.map((item) => (
                        <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>
          ))}

          <section id="delays" className="mb-12 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Common delays and what to do</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              Almost every timeline that slips, slips for one of these five reasons.
            </p>
            <div className="space-y-4">
              {DELAYS.map((d) => (
                <Card key={d.title} className="border-l-4 border-l-primary">
                  <CardContent className="p-5">
                    <div className="mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                      <h3 className="font-bold text-foreground">{d.title}</h3>
                    </div>
                    <p className="mb-2 text-sm leading-relaxed text-muted-foreground">{d.what}</p>
                    <p className="text-sm leading-relaxed text-foreground">
                      <span className="font-semibold">What to do: </span>
                      {d.doThis}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section id="closing-checklist" className="mb-10 scroll-mt-24">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Closing week checklist</h2>
            <p className="mb-5 text-sm text-muted-foreground">
              Work this list in the days after your final walkthrough is scheduled and before you sit down to sign.
            </p>
            <Card>
              <CardContent className="p-5">
                <dl className="space-y-4">
                  {CLOSING_CHECKLIST.map((c) => (
                    <div key={c.title}>
                      <dt className="flex items-center gap-2 text-sm font-bold text-foreground">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                        {c.title}
                      </dt>
                      <dd className="pl-6 text-sm leading-relaxed text-muted-foreground">{c.body}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </section>

          <Card className="mb-12 border-primary/30 bg-primary/5 print:hidden">
            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:p-6">
              <div>
                <p className="mb-1 font-bold text-foreground">Want this mapped to your actual dates?</p>
                <p className="text-sm text-muted-foreground">
                  Get a personalized timeline, a cash-to-close estimate, and the assistance programs you may qualify
                  for &mdash; free, and no credit impact.
                </p>
              </div>
              <Button asChild className="shrink-0 sm:ml-auto" onClick={() => handleLeadStart("closing_week_cta")}>
                <Link to="/auth?returnTo=/dashboard">
                  Build my timeline
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <section id="faq" className="mb-12 scroll-mt-24">
            <h2 className="mb-4 text-2xl font-bold text-foreground">Common questions</h2>
            <Accordion
              type="single"
              collapsible
              value={openFaq}
              onValueChange={handleFaqChange}
              className="rounded-xl border border-border bg-card px-4 [&_[role=region][data-state=closed]]:hidden"
            >
              {FAQS.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id}>
                  <AccordionTrigger className="text-left text-base font-semibold">{faq.q}</AccordionTrigger>
                  <AccordionContent
                    forceMount
                    className="text-sm leading-relaxed text-muted-foreground"
                  >
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="mb-6 print:hidden">
            <h2 className="mb-3 text-lg font-bold text-foreground">Keep going</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/tools/loan-officer-questions" className="text-primary hover:underline">
                Questions to ask a loan officer
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/mortgage-preapproval-meeting" className="text-primary hover:underline">
                What to expect at your pre-approval meeting
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/compare-lender-quotes" className="text-primary hover:underline">
                Compare lender quotes
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/raise-credit-score-before-mortgage" className="text-primary hover:underline">
                Raise your score before applying
              </Link>
              <span className="text-muted-foreground">&middot;</span>
              <Link to="/tools/low-income-first-time-buyer-grants" className="text-primary hover:underline">
                First-time buyer grants
              </Link>
            </div>
          </section>

          <p className="text-xs text-muted-foreground">
            Educational information only &mdash; not legal, financial, or tax advice, and not an offer of credit or a
            commitment to lend. Timelines, fees, agent compensation arrangements, and closing practices vary by
            lender, market, and state, and contract deadlines control in every transaction. Confirm every date and
            cost with your lender, agent, and closing agent.
          </p>
        </div>
      </main>

      <RelatedTools currentPath="/tools/home-buying-timeline" />
      <Footer />
    </div>
  );
};

export default HomeBuyingTimeline;
